/**
 * Claim detail's own shell: heading, filters, the Ledger's count line, export, table,
 * load-more.
 *
 * Forked from DetailPage (shared with Touch detail, append-only) because Claim detail
 * departs from it: the count line is the page's own sentence — "N claims of M on file
 * · $X labor" — rather than a generic row count; and sorting runs over every filtered
 * claim, not just the page on screen.
 *
 * DataTable sorts the rows it is handed, which here is one page of 500. So the shell
 * owns the sort: it reads header clicks, sorts the whole filtered set, and hands the
 * table a page that is already in order, remounting it with the same key and direction
 * so the header shows the right arrow. The table re-sorting an already-sorted page by
 * the same comparator is a no-op.
 */
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { DataTable, type Column } from './Table'
import { useUrlState, sortSpec } from '@/ui/urlState'
import { PopOut } from './PopOut'
import { copyRows, downloadCSV, downloadXLSXBook, type ExportColumn, type XLSXSheet } from '@/ui/export'
import { int } from '@/format'

/** The Ledger shows 200 claims first, then 500 per click. */
const FIRST = 200
const PAGE = 500

export function ClaimDetailShell<T>({
  title,
  grain,
  filters,
  summary,
  rows,
  columns,
  exportColumns,
  exportName,
  book,
  rowKey,
  onRowClick,
  message,
}: {
  title: string
  grain: string
  filters: ReactNode
  /** The count line above the table. */
  summary: ReactNode
  rows: T[]
  columns: Column<T>[]
  exportColumns: ExportColumn<T>[]
  exportName: string
  /**
   * A second export: an Excel workbook built from the sorted rows (Claim detail's
   * claims + touches, review #968). `build` returns null while its data is loading.
   */
  book?: {
    label: string
    title: string
    filename: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    build: (rows: T[]) => XLSXSheet<any>[] | null
  }
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  /**
   * Loading or error text, shown in place of the table. With no message and no rows,
   * the table keeps its headers and simply has no rows — as the Ledger's does.
   */
  message?: ReactNode
}) {
  const [shown, setShown] = useState(FIRST)
  const [flash, setFlash] = useState<string | null>(null)
  // In the URL (`?sort=col.dir`), so Back from a drill and a copied link keep the order.
  const [sort, setSort] = useUrlState('sort', null, sortSpec)

  const sorted = useMemo(() => {
    const get = columns.find((c) => c.key === sort?.key)?.sort
    if (!sort || !get) return rows
    // Keys computed once per row, not once per comparison.
    const keyed = rows.map((r) => ({ k: get(r), r }))
    const sign = sort.dir === 'asc' ? 1 : -1
    keyed.sort((a, b) => {
      // Nulls last in both directions, as DataTable does.
      if (a.k === null) return b.k === null ? 0 : 1
      if (b.k === null) return -1
      const c =
        typeof a.k === 'number' && typeof b.k === 'number'
          ? a.k - b.k
          : String(a.k).localeCompare(String(b.k))
      return sign * c
    })
    return keyed.map((x) => x.r)
  }, [rows, columns, sort])

  // A new filter or sort starts again from the top page, as hers does.
  useEffect(() => setShown(FIRST), [rows, sort])

  const visible = useMemo(() => sorted.slice(0, shown), [sorted, shown])
  const more = sorted.length - visible.length

  /** Header clicks, read before DataTable's own handler; same toggle rules as it. */
  const onHeaderClick = (e: React.MouseEvent) => {
    // A column resize ends in a click on its handle, which is not a sort.
    if ((e.target as HTMLElement).closest('.dt-resize')) return
    const th = (e.target as HTMLElement).closest('th')
    if (!th || !th.parentElement) return
    const c = columns[Array.prototype.indexOf.call(th.parentElement.children, th)]
    if (!c?.sort) return
    setSort((s) =>
      s?.key === c.key
        ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key: c.key, dir: c.align === 'right' ? 'desc' : 'asc' },
    )
  }

  const say = (msg: string) => {
    setFlash(msg)
    setTimeout(() => setFlash(null), 3000)
  }

  const onCopy = async () => {
    const r = await copyRows(sorted, exportColumns)
    say(r === 'ok' ? 'Copied' : 'This view cannot use the clipboard.')
  }

  const onCsv = () => {
    const r = downloadCSV(exportName, sorted, exportColumns)
    say(r === 'ok' ? 'Saved' : 'This view cannot start a download — use Copy instead.')
  }

  const onBook = () => {
    if (!book) return
    const sheets = book.build(sorted)
    if (!sheets) return say('The touches are still loading. Try again in a moment.')
    const r = downloadXLSXBook(book.filename, sheets)
    say(r === 'ok' ? 'Saved' : 'This view cannot start a download.')
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 className="page-title">{title}</h1>
        <span className="pts-chip neut">{grain}</span>
      </div>

      {/* The filters come along into the enlarged view — Shayna's #64 screenshot is this
          table, and narrowing it is half of what she does with it. */}
      <PopOut title={title} note={grain}>
        {({ button }) => (
          <>
            <div style={{ marginTop: 16 }}>{filters}</div>

            <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '14px 0 0' }}>{summary}</p>

            {message ? (
              <div className="card" style={{ marginTop: 10, padding: 0 }}>
                <div className="empty-note">{message}</div>
              </div>
            ) : (
              <>
                <div className="xbar" style={{ marginTop: 6 }}>
                  {button}
                  <span style={{ marginLeft: 8 }}>Export</span>
                  <button type="button" onClick={() => void onCopy()}>
                    Copy
                  </button>
                  <button type="button" onClick={onCsv}>
                    CSV
                  </button>
                  {book && (
                    <button type="button" onClick={onBook} title={book.title} data-testid="export-book">
                      {book.label}
                    </button>
                  )}
                  {flash && <em>{flash}</em>}
                </div>

                <div className="popout-fill">
                  <div
                    className="card"
                    style={{ marginTop: 10, padding: 0, overflow: 'hidden' }}
                    onClickCapture={onHeaderClick}
                  >
                    {rows.length === 0 ? (
                      // DataTable swaps an empty table for a sentence; hers keeps the headers.
                      <div className="dt-wrap">
                        <table className="dt">
                          <thead>
                            <tr>
                              {columns.map((c) => (
                                <th key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                                  {c.header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                        </table>
                      </div>
                    ) : (
                      <DataTable
                        storageKey="claim-detail.rows"
                        key={sort ? `${sort.key}:${sort.dir}` : 'unsorted'}
                        initialSort={sort?.key}
                        initialDir={sort?.dir}
                        rows={visible}
                        columns={columns}
                        rowKey={rowKey}
                        onRowClick={onRowClick}
                        maxHeight="72vh"
                      />
                    )}
                  </div>
                </div>

                {more > 0 && (
                  <button
                    type="button"
                    className="reset-btn"
                    onClick={() => setShown((n) => n + PAGE)}
                    style={{ marginTop: 10, alignSelf: 'flex-start' }}
                  >
                    Show {int(Math.min(PAGE, more))} more of {int(more)} remaining
                  </button>
                )}
              </>
            )}
          </>
        )}
      </PopOut>
    </>
  )
}
